import { useCallback, useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import type { FileReadResult, FileWriteResult, WorkspaceRoutingClass } from '@localagent/sdk';
import { getSDK, useDeviceId } from '../services/sdk-instance';
import { workspaceProjectionAtomFamily } from '../stores/workspace/projection';
import { useWakeWorkspace } from './useWorkspaces';
import { workspaceFilesAvailability } from './workspaceFilesAvailability';

export interface WorkspaceFileEntry {
  path: string;
  name: string;
  isDir: boolean;
  size: number;
  /**
   * Per-file content version token (sha256 from core file_ops) as returned by
   * the cloud sandbox / local daemon `list` ops — the change-detection currency
   * for tree refresh. Null for directories.
   */
  version: string | null;
}

export type WorkspaceFilesState =
  | 'loading'
  | 'ready'
  | 'sleeping'
  | 'waking'
  | 'no-pod'
  | 'other-computer'
  | 'this-computer-offline'
  | 'error';

/**
 * Map one raw sandbox/daemon list entry (whose `name` is the workspace-relative
 * path) to the display shape. Shared verbatim by the cloud and local arms — the
 * daemon now returns the same `{name(=rel path), is_dir, size}` shape as the
 * cloud sandbox.
 */
function mapEntry(e: {
  name: string;
  size?: number;
  is_dir?: boolean;
  version?: string | null;
}): WorkspaceFileEntry {
  return {
    path: e.name,
    name: e.name.split('/').pop() || e.name,
    isDir: e.is_dir === true,
    size: e.size ?? 0,
    version: e.version ?? null,
  };
}

export function useWorkspaceFiles(
  workspaceId: string | null,
  workspacePath?: string | null,
  mode?: WorkspaceRoutingClass
) {
  const isLocal = mode === 'local';
  const modeKnown = mode !== undefined;
  const projection = useAtomValue(workspaceProjectionAtomFamily(workspaceId ?? '')).projection;
  const thisDeviceId = useDeviceId();
  const wakeWorkspace = useWakeWorkspace(workspaceId ?? '');
  const wakeWorkspaceMutateAsync = wakeWorkspace.mutateAsync;

  const [state, setState] = useState<WorkspaceFilesState>('loading');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!modeKnown || !workspaceId || !projection || (isLocal && thisDeviceId === null)) {
      setState('loading');
      setError(null);
      return;
    }
    const availability = workspaceFilesAvailability(projection, thisDeviceId);
    if (availability.state === 'ready' && isLocal && !workspacePath) {
      setError('Local file browsing requires a workspace path');
      setState('error');
      return;
    }
    setState(availability.state);
    setError('message' in availability ? availability.message : null);
  }, [isLocal, modeKnown, projection, thisDeviceId, workspaceId, workspacePath]);

  const ensureRunning = useCallback(async (): Promise<void> => {
    if (!workspaceId) throw new Error('File browsing requires a workspace');
    if (!projection) throw new Error('Workspace projection is unavailable');
    if (projection.capabilities.execution && projection.capabilities.content_readable) {
      setState('ready');
      return;
    }
    if (projection.assignment.routing_class === 'local') {
      const save = projection.actions.save;
      throw new Error(save.available ? 'Workspace files are not readable yet' : save.refusal.message);
    }
    if (!projection.actions.wake.available) {
      throw new Error(projection.actions.wake.refusal.message);
    }
    const awakened = await wakeWorkspaceMutateAsync(
      `file-browser-wake-${workspaceId}-${projection.revision}`
    );
    if (awakened.capabilities.execution && awakened.capabilities.content_readable) {
      setState('ready');
      return;
    }
    if (awakened.workstation.status === 'starting') {
      setState('waking');
      return;
    }
    setState('error');
    throw new Error('Workspace wake completed without execution and content-read capability');
  }, [projection, wakeWorkspaceMutateAsync, workspaceId]);

  const listDir = useCallback(
    async (path = ''): Promise<WorkspaceFileEntry[]> => {
      await ensureRunning();
      if (!workspaceId) {
        throw new Error('listDir requires a workspace');
      }
      const res = await getSDK().workspaces.executeFileOp(workspaceId, {
        op_type: 'list',
        path,
      });
      if (res.status === 'error' || res.error) {
        throw new Error(`listDir(${path || '/'}) failed: ${res.error ?? 'unknown error'}`);
      }
      const raw = (res.metadata as { entries?: unknown } | undefined)?.entries;
      if (!Array.isArray(raw)) {
        throw new Error(`listDir(${path || '/'}): sandbox returned no entries array`);
      }
      return (
        raw as Array<{ name: string; size?: number; is_dir?: boolean; version?: string | null }>
      ).map(mapEntry);
    },
    [workspaceId, ensureRunning]
  );

  const readFile = useCallback(
    async (path: string): Promise<FileReadResult> => {
      await ensureRunning();
      if (!workspaceId) {
        throw new Error('readFile requires a workspace');
      }
      return getSDK().workspaces.readFile(workspaceId, path);
    },
    [workspaceId, ensureRunning]
  );

  const writeFile = useCallback(
    async (path: string, content: string, expectedVersion?: string): Promise<FileWriteResult> => {
      await ensureRunning();
      if (!workspaceId) {
        throw new Error('writeFile requires a workspace');
      }
      return getSDK().workspaces.writeFile(workspaceId, path, content, expectedVersion);
    },
    [workspaceId, ensureRunning]
  );

  const createFile = useCallback(
    async (path: string, content: string): Promise<FileWriteResult> => {
      await ensureRunning();
      if (!workspaceId) {
        throw new Error('createFile requires a workspace');
      }
      return getSDK().workspaces.createFile(workspaceId, path, content);
    },
    [workspaceId, ensureRunning]
  );

  const deleteFile = useCallback(
    async (path: string): Promise<{ deleted: boolean }> => {
      await ensureRunning();
      if (!workspaceId) {
        throw new Error('deleteFile requires a workspace');
      }
      return getSDK().workspaces.deleteFile(workspaceId, path);
    },
    [workspaceId, ensureRunning]
  );

  const moveFile = useCallback(
    async (path: string, destination: string): Promise<{ moved: boolean }> => {
      await ensureRunning();
      if (!workspaceId) {
        throw new Error('moveFile requires a workspace');
      }
      return getSDK().workspaces.moveFile(workspaceId, path, destination);
    },
    [workspaceId, ensureRunning]
  );

  const createFolder = useCallback(
    async (path: string): Promise<{ version: string }> => {
      await ensureRunning();
      if (!workspaceId) {
        throw new Error('createFolder requires a workspace');
      }
      return getSDK().workspaces.createFolder(workspaceId, path);
    },
    [workspaceId, ensureRunning]
  );

  return {
    state,
    error,
    available: state === 'ready',
    listDir,
    readFile,
    writeFile,
    createFile,
    deleteFile,
    moveFile,
    createFolder,
    resume: ensureRunning,
  };
}
